import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faFileLines, faGear, faInbox, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FilterChips, PageHeader, ScopeNote } from '../../components';
import { useAuth } from '../../context/AuthContext';
import { useRecordTemplateUse } from '../../queries/templates';
import { useMeQuery } from '../../queries/me';
import { useMaxWidth, usePhone } from '../../hooks/usePhone';
import { TABLET } from '../../theme/tokens';
import MessagesTab from './MessagesTab';
import TemplatesTab from './TemplatesTab';
import SmartReplyTab from './SmartReplyTab';
import SettingsTab from './SettingsTab';
import { PrimaryBtn, TabsActions, TabsRow, TitleRow } from './styled';

const SMS_OFF = {
  missing_business_info: 'Texting turns on once your business details are complete. Until then, messages can be read but not sent.',
  pending_approval: 'Texting is waiting for carrier approval, which usually takes a few business days. Until then, messages can be read but not sent.'
};

const TABS = [
  { key: 'messages', label: 'Messages', icon: <FontAwesomeIcon icon={faInbox} /> },
  { key: 'templates', label: 'Templates', icon: <FontAwesomeIcon icon={faFileLines} /> },
  { key: 'smart', label: 'Smart Reply', icon: <FontAwesomeIcon icon={faBolt} /> },
  { key: 'settings', label: 'Settings', icon: <FontAwesomeIcon icon={faGear} /> }
];

export default function Inbox() {
  const { user, can } = useAuth();
  const { data: me } = useMeQuery();
  const smsOff = me?.sms && !me.sms.available ? SMS_OFF[me.sms.reason] || SMS_OFF.pending_approval : null;
  const canSend = can('manage_messages') && !smsOff;
  const canManageSettings = can('manage_settings');
  const [tab, setTab] = useState('messages');
  const [draft, setDraft] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const recordUse = useRecordTemplateUse();
  const compact = useMaxWidth(TABLET);
  const phone = usePhone();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const activeNumber = search.get('contact') || search.get('c') || location.state?.contactNumber || null;
  const detail = compact && tab === 'messages' && Boolean(activeNumber);

  const setNumber = (contactNumber, replace) =>
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (contactNumber) next.set('c', contactNumber);
        else next.delete('c');
        return next;
      },
      { replace }
    );
  const selectNumber = (contactNumber) => setNumber(contactNumber, !compact);
  const back = () => (location.key === 'default' ? setNumber(null, true) : navigate(-1));

  const insertTemplate = (template) => {
    setDraft((d) => (d.trim() ? `${d.trimEnd()} ${template.body}` : template.body));
    recordUse.mutate(template._id);
  };

  const openInComposer = (template) => {
    insertTemplate(template);
    setTab('messages');
  };

  return (
    <div data-fill={detail || undefined}>
      {!detail && (
        <>
          {phone ? (
            <TitleRow>
              <PageHeader title="Inbox" />
              {tab === 'messages' && (
                <PrimaryBtn type="button" $tall onClick={() => setNewOpen(true)} disabled={!canSend}>
                  <FontAwesomeIcon icon={faPen} />
                  New message
                </PrimaryBtn>
              )}
            </TitleRow>
          ) : (
            <PageHeader title="Inbox" subtitle="Text tenants, prospects, owners, and vendors. AI drafts the replies, you approve them." />
          )}

          {smsOff && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="SMS is not active yet" description={smsOff} />}

          <TabsRow>
            <FilterChips bare label="Inbox sections" options={TABS} value={tab} onChange={setTab} />
            {tab === 'messages' && phone && <ScopeNote />}
            {tab === 'messages' && !phone && (
              <TabsActions>
                <ScopeNote />
                <PrimaryBtn type="button" $tall onClick={() => setNewOpen(true)} disabled={!canSend}>
                  <FontAwesomeIcon icon={faPlus} />
                  New Message
                </PrimaryBtn>
              </TabsActions>
            )}
          </TabsRow>
        </>
      )}

      {tab === 'messages' && (
        <MessagesTab
          activeNumber={activeNumber}
          onSelectNumber={selectNumber}
          onBack={compact ? back : null}
          canSend={canSend}
          draft={draft}
          onDraftChange={setDraft}
          onTemplatePick={insertTemplate}
          newOpen={newOpen}
          onCloseNew={() => setNewOpen(false)}
        />
      )}
      {tab === 'templates' && (
        <TemplatesTab currentUserId={user?._id || user?.id} canManage={canSend} onUse={openInComposer} />
      )}
      {tab === 'smart' && <SmartReplyTab canManage={canManageSettings} />}
      {tab === 'settings' && <SettingsTab canManage={canManageSettings} />}
    </div>
  );
}
